<template>
  <div>
    <el-row :gutter="15">
      <el-col :span="8">
        <el-card>
          <div slot="header">
            {{ $t('homePage.userCount') }}
          </div>
          <div class="statistics">
            <bean-link :to="{ name: 'users.index' }">{{ data.users_count }}</bean-link>
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card>
          <div slot="header">
            {{ $t('homePage.activedUserCount') }}
          </div>
          <div class="statistics">
            {{ data.actived_user_count }}
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card>
          <div slot="header">
            {{ $t('masterLedger.total_number_of_transactions') }}
          </div>
          <div class="statistics">
            <bean-link :to="{ name: 'transaction_history.index' }">{{ data.transactions_count }}</bean-link>
          </div>
        </el-card>
      </el-col>
    </el-row>
    <el-row :gutter="15">
      <el-col :span="8">
        <el-card>
          <div slot="header">
            {{ $t('homePage.agentCount') }}
          </div>
          <div class="statistics">
            <bean-link :to="{ name: 'agents.index' }">{{ data.agents_count }}</bean-link>
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card>
          <div slot="header">
            {{ $t('homePage.activedAgentCount') }}
          </div>
          <div class="statistics">
            {{ data.actived_agent_count }}
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card>
          <div slot="header">
            {{ $t('homePage.pendingStoreOwner') }}
          </div>
          <div class="statistics">
            {{ data.pending_store_owner_count }}
          </div>
        </el-card>
      </el-col>
    </el-row>
    <el-row :gutter="15">
      <el-col :span="8">
        <el-card>
          <div slot="header">
            {{ $t('homePage.pendingUserVerification') }}
          </div>
          <div class="statistics">
            <bean-link :to="{ name: 'user_verifications.index', query: { verification_state_eq: 'pending' } }">
              {{ data.pending_user_verification_count }}
            </bean-link>
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card>
          <div slot="header">
            {{ $t('homePage.pendingMerchantWithdrawalRequest') }}
          </div>
          <div class="statistics">
            {{ data.pending_merchant_withdrawal_count }}
          </div>
        </el-card>
      </el-col>
    </el-row>
    <el-row :gutter="15">
      <el-col :span="12">
        <el-card>
          <div slot="header">
            {{ $t('homePage.newUserTrend') }}
          </div>
          <div>
            <ECharts :options="newUsersTrendLineOptions" style="width:100%" />
          </div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card>
          <div slot="header">
            {{ $t('homePage.newAgentTrend') }}
          </div>
          <div>
            <ECharts :options="newAgentsTrendLineOptions" style="width:100%" />
          </div>
        </el-card>
      </el-col>
    </el-row>
    <el-row :gutter="15">
      <el-col :span="12">
        <el-card>
          <div slot="header">
            {{ $t('homePage.newTransactionsTrend') }}
          </div>
          <div>
            <ECharts :options="newTransactionsTrendLineOptions" style="width:100%" />
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator';
import _ from 'lodash';
import ECharts from 'vue-echarts';
import 'echarts/lib/chart/line';
import 'echarts/lib/component/legendScroll';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/title';

@Component({
  components: {
    ECharts
  }
})
export default class ApplicationHome extends Vue {
  data = {
    users_count: 0,
    new_users_trend: [],
    new_admin_roles_users_trend: []
  }

  chartBaseConfig = {
    tooltip: {
      trigger: 'axis'
    },
    legend: {
      data: []
    },
    xAxis: {
      type: 'category',
      data: []
    },
    yAxis: {
      type: 'value'
    },
    series: []
  }

  async mounted() {
    this.data = await this.$request.get('/dashboard/application')
  }

  get newUsersTrendLineOptions() {
    const legendData = [this.$t('homePage.newUserCount'), this.$t('homePage.newActivedUserCount')]

    return _.merge({}, this.chartBaseConfig, {
      legend: {
        data: legendData
      },
      xAxis: {
        data: Object.keys(this.data.new_users_trend)
      },
      yAxis: {
        minInterval: 1
      },
      series: _.map([
        this.data.new_users_trend,
        this.data.new_actived_users_trend
      ], (element, index) => {
        return {
          name: legendData[index],
          type: 'line',
          data: Object.values(element)
        }
      })
    })
  }

  get newAgentsTrendLineOptions() {
    const legendData = [this.$t('homePage.newAgentCount'), this.$t('homePage.newActivedAgentCount')]

    return _.merge({}, this.chartBaseConfig, {
      legend: {
        data: legendData
      },
      xAxis: {
        data: Object.keys(this.data.new_agents_trend)
      },
      yAxis: {
        minInterval: 1
      },
      series: _.map([
        this.data.new_agents_trend,
        this.data.new_actived_agents_trend
      ], (element, index) => {
        return {
          name: legendData[index],
          type: 'line',
          data: Object.values(element)
        }
      })
    })
  }

  get newTransactionsTrendLineOptions() {
    const legendData = [this.$t('homePage.newTransactionsCount')]

    return _.merge({}, this.chartBaseConfig, {
      legend: {
        data: legendData
      },
      xAxis: {
        data: Object.keys(this.data.transactions_count_trend)
      },
      yAxis: {
        minInterval: 1
      },
      series: _.map([
        this.data.transactions_count_trend,
      ], (element, index) => {
        return {
          name: legendData[index],
          type: 'line',
          data: Object.values(element)
        }
      })
    })
  }
}
</script>
